import { SelectionModel } from '@angular/cdk/collections';
import { Observable, Subject, Subscription } from 'rxjs';

/** A collection can display an existing owner's selection without copying it.
 * Writes are deltas for explicit rows, never a replacement with the loaded page. */
export interface DataSelectionBinding {
  readonly key: string;
  readonly changed$: Observable<unknown>;
  /** Total held by the owner, including rows that this view has not loaded. */
  readonly selectedCount?: number;
  has(id: unknown): boolean;
  setSelected(ids: readonly unknown[], selected: boolean): void;
}

/** Shared table/list selection access. Bound mode is a live projection; only
 * ordinary, unbound collections keep a local CDK SelectionModel. */
export class DataViewSelection<T> {
  private readonly local = new SelectionModel<T>(true, []);
  private readonly changes = new Subject<void>();
  readonly changed = this.changes.asObservable();
  private readonly localChanges = this.local.changed.subscribe(() => this.changes.next());
  private ownerChanges?: Subscription;
  private owner: DataSelectionBinding | null = null;

  constructor(private readonly rows: () => readonly T[], private readonly id: (row: T) => unknown) {}

  get binding(): DataSelectionBinding | null { return this.owner; }
  set binding(owner: DataSelectionBinding | null) {
    if (owner === this.owner) return;
    this.ownerChanges?.unsubscribe();
    this.owner = owner;
    this.local.clear();
    this.ownerChanges = owner?.changed$.subscribe(() => this.changes.next());
    this.changes.next();
  }

  /** Display total only; selected/clear retain their loaded-row command scope. */
  get count(): number { return this.owner?.selectedCount ?? this.selected.length; }

  get selected(): T[] {
    return this.owner ? this.rows().filter(row => this.isSelected(row)) : this.local.selected;
  }
  isSelected(row: T): boolean { return this.owner ? this.owner.has(this.id(row)) : this.local.isSelected(row); }
  hasValue(): boolean { return this.selected.length > 0; }
  isEmpty(): boolean { return !this.hasValue(); }
  select(...rows: T[]): void {
    if (this.owner) this.owner.setSelected(rows.map(this.id), true);
    else this.local.select(...rows);
  }
  deselect(...rows: T[]): void {
    if (this.owner) this.owner.setSelected(rows.map(this.id), false);
    else this.local.deselect(...rows);
  }
  toggle(row: T): void {
    if (this.isSelected(row)) this.deselect(row);
    else this.select(row);
  }
  /** User action: only the selected rows represented by this view. */
  clear(): void {
    if (this.owner) this.deselect(...this.selected);
    else this.local.clear();
  }
  /** Query/lifecycle replacement must never change an external owner. */
  resetLocal(): void { this.local.clear(); }
  rowsChanged(): void { if (this.owner) this.changes.next(); }
  destroy(): void {
    this.ownerChanges?.unsubscribe();
    this.localChanges.unsubscribe();
    this.changes.complete();
  }
}
